import { Link } from "react-router-dom";
import { usePortalMe, usePortalOpportunities } from "../portal/api.js";
import { OpportunityCard } from "../portal/OpportunityCard.js";

/**
 * Opportunities tab. Routes the account through the unit-16 states:
 *   - no linked record  -> apply CTA (the no-match path)
 *   - claimConflict     -> support notice (email linked to another login)
 *   - PENDING_REVIEW    -> application received / under review
 *   - APPROVED          -> opportunities (placeholder until unit 17)
 */
export function PortalOpportunities() {
  const { data: me, isPending, isError, refetch } = usePortalMe();

  if (isPending) {
    return <div className="mx-auto max-w-md px-6 py-10 text-center text-charcoal">Loading…</div>;
  }
  if (isError) {
    return (
      <div className="mx-auto max-w-md px-6 py-10 text-center">
        <p className="text-charcoal">We couldn&rsquo;t load your account right now.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-xl bg-brand-700 px-6 text-sm font-semibold text-white"
        >
          Try again
        </button>
      </div>
    );
  }

  if (me.claimConflict) {
    return (
      <Notice heading="This email is linked to another account" label="Needs attention">
        A pro profile with your email address is already connected to a different login. If that
        profile is yours, reply to any of our emails and we&rsquo;ll sort it out.
      </Notice>
    );
  }

  if (!me.contractor) {
    return (
      <div className="mx-auto max-w-md px-6 py-10">
        <h1 className="text-xl font-bold text-charcoal">
          {me.givenName ? `Welcome, ${me.givenName}!` : "Welcome!"}
        </h1>
        <div className="mt-6 space-y-4 rounded-2xl border border-charcoal/15 bg-white p-6">
          <p className="text-base leading-relaxed text-charcoal">
            Your account is set up. To start receiving job invitations, tell us about your business
            — we review every pro before matching.
          </p>
          <Link
            to="/portal/apply"
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800"
          >
            Apply to get matched
          </Link>
          <p className="text-sm leading-relaxed text-charcoal">
            Already quoting through email invitations? Those links keep working — and if you sign up
            with the same email address your invitations are linked automatically.
          </p>
        </div>
      </div>
    );
  }

  if (me.contractor.status !== "APPROVED") {
    return <PendingReview businessName={me.contractor.businessName} />;
  }

  return <Dashboard />;
}

/**
 * Not-yet-approved accounts aren't matchable, but they can already OWN work:
 * an ops admin can hand-invite a pro to a request before any application, and
 * the winner email tells them to sign up to manage the job. Never hide an
 * existing invitation or won job behind the review gate — show the dashboard
 * with an in-review banner instead. Zero invitations keeps the plain notice.
 */
function PendingReview({ businessName }: { businessName: string }) {
  const { data, isPending } = usePortalOpportunities();
  const hasWork = (data?.items.length ?? 0) > 0;

  if (!isPending && !hasWork) {
    return (
      <Notice heading={`Thanks, ${businessName}!`} label="Under review">
        Your application is in review. We&rsquo;ll email you as soon as you&rsquo;re approved — no
        need to check back here.
      </Notice>
    );
  }
  return (
    <div>
      {hasWork && (
        <div className="mx-auto max-w-md px-6 pt-6">
          <p className="rounded-2xl border border-charcoal/15 bg-white p-4 text-sm leading-relaxed text-charcoal">
            <span className="font-semibold">Your application is in review.</span> New job matches
            start once you&rsquo;re approved — your existing invitations and jobs are below.
          </p>
        </div>
      )}
      <Dashboard />
    </div>
  );
}

/** The unit-17 list: every invitation, live ones first, history below. */
function Dashboard() {
  const { data, isPending, isError, refetch } = usePortalOpportunities();

  if (isPending) {
    return (
      <div className="mx-auto max-w-md px-6 py-10">
        <div className="h-6 w-1/2 animate-pulse rounded bg-charcoal/15" />
        <div className="mt-4 h-24 animate-pulse rounded-2xl bg-charcoal/15" />
        <div className="mt-3 h-24 animate-pulse rounded-2xl bg-charcoal/15" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="mx-auto max-w-md px-6 py-10 text-center">
        <p className="text-charcoal">We couldn&rsquo;t load your opportunities right now.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-xl bg-brand-700 px-6 text-sm font-semibold text-white"
        >
          Try again
        </button>
      </div>
    );
  }

  const open = data.items.filter((i) => i.outcome === "open");
  const past = data.items.filter((i) => i.outcome !== "open");

  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <h1 className="px-2 text-xl font-bold text-charcoal">Opportunities</h1>

      {data.items.length === 0 && (
        <div className="mt-6 rounded-2xl border border-charcoal/15 bg-white p-6 text-center">
          <p className="text-base text-charcoal">
            No job invitations yet. When a request matches your services and area, it lands here and
            in your email at the same time.
          </p>
        </div>
      )}

      {open.length > 0 && (
        <section className="mt-4 space-y-3">
          {open.map((item) => (
            <OpportunityCard key={item.invitationId} item={item} />
          ))}
        </section>
      )}

      {past.length > 0 && (
        <section className="mt-8">
          <h2 className="px-2 text-xs font-semibold uppercase tracking-wide text-charcoal">Past</h2>
          <div className="mt-2 space-y-3">
            {past.map((item) => (
              <OpportunityCard key={item.invitationId} item={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Notice({
  heading,
  label,
  children,
}: {
  heading: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-md px-6 py-10">
      <h1 className="text-xl font-bold text-charcoal">{heading}</h1>
      <div className="mt-6 space-y-4 rounded-2xl border border-charcoal/15 bg-white p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-amber-600">{label}</p>
        <p className="text-base leading-relaxed text-charcoal">{children}</p>
      </div>
    </div>
  );
}
